import {
    type ApprovalProcess,
    type ApprovalScopeEnum,
    type AutoMatchPurchaseOrdersRequest,
    type AutoMatchPurchaseOrdersResponse,
    type CommentItem,
    type ExpenseTag,
    type ExpenseTagColorEnum,
    type PurchaseOrderDTO,
    type QueryParamsObject,
} from "@finway-group/shared/lib/models"
import { type AggregatePaginateResult } from "@finway-group/shared/lib/models/common/aggregatePaginateResult"
import { type PurchaseOrderApprovalProcessDTO } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderDTO"
import { PurchaseOrderActions } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatusAction.enum"
import { type NestedKeyOf } from "@finway-group/shared/lib/utils/common"
import { getUniqueElements } from "@finway-group/shared/lib/utils/helpers"
import { useMutation, useQuery, type UseQueryOptions } from "@tanstack/react-query"
import { t } from "i18next"
import { useHistory } from "react-router-dom"
import { defaultSortParams } from "~Features/pages/deliveryNotes/queries/deliveryNote.queries"
import { type ILineItem } from "~Features/pages/purchaseOrders/types/LineItems.interfaces"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { i18n } from "~Shared/locales/i18n"
import { invalidatePerformanceExpenses } from "~Shared/queries/analytics/performanceExpenses.queries"
import { CacheEnum, GENERAL_QUERY_STALE_TIME, type QueryParams } from "~Shared/queries/consts"
import { getSortQueryObject } from "~Shared/queries/query.utils"
import { CompanyScopedRoutePathEnum } from "~Shared/router/RoutePath.enum"
import { ExpenseTagService, FileService, NotificationService } from "~Shared/services"
import { NotificationDurationSec, NotificationTypeEnum } from "~Shared/services/notification.service"
import { store } from "~Shared/store"
import { refetchTable } from "~Shared/store/actions/tables/tableActions"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { convertSearchValueToQueryObject, isSet } from "~Shared/utils/helper.utils"
import { generatePath } from "~Shared/utils/routing.utils"
import { PurchaseOrderApiService, type PurchaseOrderTransitionRequest } from "../service/purchaseOrderApi.service"
import { type PurchaseOrder, type PurchaseOrderForLinking, type PurchaseOrderLinks } from "../types/PurchaseOrder.interfaces"

export interface PurchaseOrderListFilters {
    limit?: number
}

const poSearchFields: Array<NestedKeyOf<PurchaseOrder>> = ["purchaseOrderNumber", "description", "vendor.name"]

// Query keys
export const PURCHASE_ORDER_QUERY_KEYS = {
    ALL: [CacheEnum.PURCHASE_ORDERS] as const,
    LIST: (filters: QueryParams) => [CacheEnum.PURCHASE_ORDERS, { filters }] as const,
    DETAIL: (id: string) => [...PURCHASE_ORDER_QUERY_KEYS.ALL, "detail", id] as const,
    LIST_FOR_LINKING: (filters: QueryParams) => [CacheEnum.PURCHASE_ORDERS, "for-linking", { filters }] as const,
    LINKS: (id: string) => [...PURCHASE_ORDER_QUERY_KEYS.ALL, "links", id] as const,
} as const

// Main purchase order queries
export const usePurchaseOrdersQuery = ({ page, limit, search, filter, sortParams }: QueryParams, options?: UseQueryOptions<AggregatePaginateResult<PurchaseOrder>>) => {
    const searchParam = isSet(search) ? convertSearchValueToQueryObject(search!, poSearchFields) : undefined
    const params: QueryParamsObject = {
        ...(page !== undefined && { page }),
        ...(limit !== undefined && { limit }),
        ...(searchParam || {}),
        ...(getSortQueryObject(sortParams || defaultSortParams) || {}),
        ...(filter || {}),
    }
    return useQuery<AggregatePaginateResult<PurchaseOrder>>({
        queryKey: PURCHASE_ORDER_QUERY_KEYS.LIST({ page, limit, search, filter, sortParams }),
        queryFn: ({ signal }) => PurchaseOrderApiService.fetchPurchaseOrders({ params, signal }),
        staleTime: GENERAL_QUERY_STALE_TIME,
        keepPreviousData: true,
        ...options,
    })
}

export const fetchPurchaseOrdersForLinking = ({ search, filter }: QueryParams) =>
    queryClient.fetchQuery({
        queryKey: PURCHASE_ORDER_QUERY_KEYS.LIST_FOR_LINKING({ search, filter }),
        queryFn: () => PurchaseOrderApiService.fetchPurchaseOrdersForLinking({ params: { ...filter, search } }),
    })

export const usePurchaseOrdersForLinkingQuery = ({ page, limit, search, filter }: QueryParams, options?: UseQueryOptions<AggregatePaginateResult<PurchaseOrderForLinking>>) => {
    const searchParam = isSet(search) ? convertSearchValueToQueryObject(search!, poSearchFields) : undefined
    const params: QueryParamsObject = {
        ...(page !== undefined && { page }),
        ...(limit !== undefined && { limit }),
        ...(searchParam || {}),
        ...(filter || {}),
        forLinking: true,
    }
    return useQuery<AggregatePaginateResult<PurchaseOrderForLinking>>({
        queryKey: PURCHASE_ORDER_QUERY_KEYS.LIST_FOR_LINKING({ page, limit, search, filter }),
        queryFn: ({ signal }) => PurchaseOrderApiService.fetchPurchaseOrdersForLinking({ params, signal }),
        staleTime: GENERAL_QUERY_STALE_TIME,
        keepPreviousData: true,
        ...options,
    })
}

export const usePurchaseOrderQuery = (id: string) =>
    useQuery<PurchaseOrder>({
        queryKey: PURCHASE_ORDER_QUERY_KEYS.DETAIL(id),
        queryFn: () => PurchaseOrderApiService.getPurchaseOrderById(id),
        enabled: Boolean(id),
        staleTime: GENERAL_QUERY_STALE_TIME,
    })

export const usePurchaseOrderLinksQuery = (id: string, options?: UseQueryOptions<PurchaseOrderLinks>) =>
    useQuery<PurchaseOrderLinks>({
        queryKey: PURCHASE_ORDER_QUERY_KEYS.LINKS(id),
        queryFn: () => PurchaseOrderApiService.getPurchaseOrderLinks(id),
        enabled: Boolean(id),
        staleTime: GENERAL_QUERY_STALE_TIME,
        keepPreviousData: false,
        ...options,
    })

export const useUpdatePurchaseOrderMutation = () =>
    useMutation<PurchaseOrder, Error, { id: string; po: PurchaseOrderDTO }>({
        mutationFn: ({ id, po }: { id: string; po: PurchaseOrderDTO }) => PurchaseOrderApiService.updatePurchaseOrder(id, po),
        onSuccess: async (purchaseOrder, { id }) => {
            await invalidatePurchaseOrderDetail(id)
            await invalidatePurchaseOrderLists()
            store.dispatch(refetchTable(TablesEnum.COST_CENTERS))
            await invalidatePerformanceExpensesForPurchaseOrder(purchaseOrder)

            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                i18n.t("notification:purchase_order.updated.title"),
                i18n.t("notification:purchase_order.updated.message"),
                NotificationDurationSec.REQUEST_CREATION,
            )
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:request.edit.title"))
        },
    })

// Mutations
export const useCreatePurchaseOrderMutation = () => {
    const history = useHistory()
    return useMutation<PurchaseOrder, Error, PurchaseOrderDTO>({
        mutationFn: (purchaseOrder: PurchaseOrderDTO) => PurchaseOrderApiService.createPurchaseOrder(purchaseOrder),
        onSuccess: async (purchaseOrder) => {
            // Invalidate and refetch purchase order lists
            await invalidatePurchaseOrderLists()
            store.dispatch(refetchTable(TablesEnum.COST_CENTERS))
            await invalidatePerformanceExpensesForPurchaseOrder(purchaseOrder)

            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                i18n.t("notification:purchase_order.created.title"),
                i18n.t("notification:purchase_order.created.message"),
                NotificationDurationSec.REQUEST_CREATION,
                "useCreatePurchaseOrderMutation.onSuccess",
                {
                    onClick: () => history.push(generatePath(CompanyScopedRoutePathEnum.PURCHASE_ORDER_DETAIL, { id: purchaseOrder._id })),
                    text: i18n.t("notification:purchase_order.created.link"),
                },
            )
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:request.create.title"))
        },
    })
}

export const useTransitionPurchaseOrderMutation = () =>
    useMutation<PurchaseOrder, Error, { id: string; transition: PurchaseOrderTransitionRequest }>({
        mutationFn: ({ id, transition }: { id: string; transition: PurchaseOrderTransitionRequest }) => PurchaseOrderApiService.transitionPurchaseOrder(id, transition),
        onSuccess: async (purchaseOrder, { id, transition }) => {
            // This will only invalidate the specific purchase order
            await invalidatePurchaseOrderDetail(id)
            // This will invalidate all purchase order lists
            await invalidatePurchaseOrderLists()
            store.dispatch(refetchTable(TablesEnum.COST_CENTERS))
            await invalidatePerformanceExpensesForPurchaseOrder(purchaseOrder)

            if (transition.action === PurchaseOrderActions.APPROVE || transition.action === PurchaseOrderActions.SUBMIT_FOR_FULFILLMENT) {
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:purchase_order.approved.title"), t("notification:purchase_order.approved.message"))
            } else if (transition.action === PurchaseOrderActions.DENY) {
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:purchase_order.rejected.title"), t("notification:purchase_order.rejected.message"))
            } else if (transition.action === PurchaseOrderActions.SUBMIT_FOR_APPROVAL) {
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:purchase_order.submitted.title"), t("notification:purchase_order.submitted.message"))
            } else if (transition.action === PurchaseOrderActions.REOPEN_PURCHASE_ORDER) {
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:purchase_order.reopened.title"), t("notification:purchase_order.reopened.message"))
            }
        },
        onError: (error, { transition }) => {
            if (transition.action === PurchaseOrderActions.APPROVE || transition.action === PurchaseOrderActions.SUBMIT_FOR_FULFILLMENT) {
                NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:purchase_order.approve.title"))
            } else {
                NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:error"))
            }
        },
    })

export const useCreateAndSubmitPurchaseOrderMutation = () => {
    const history = useHistory()
    return useMutation<PurchaseOrder, Error, PurchaseOrderDTO>({
        mutationFn: (purchaseOrder: PurchaseOrderDTO) => PurchaseOrderApiService.createAndSubmitPurchaseOrder(purchaseOrder),

        onSuccess: async (data: PurchaseOrder) => {
            // Invalidate and refetch purchase order lists
            await invalidatePurchaseOrderLists()
            store.dispatch(refetchTable(TablesEnum.COST_CENTERS))
            await invalidatePerformanceExpensesForPurchaseOrder(data)

            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                i18n.t("notification:purchase_order.createdAndSubmitted.title"),
                i18n.t("notification:purchase_order.createdAndSubmitted.message"),
                NotificationDurationSec.REQUEST_CREATION,
                "useCreateAndSubmitPurchaseOrderMutation.onSuccess",
                {
                    onClick: () => history.push(generatePath(CompanyScopedRoutePathEnum.PURCHASE_ORDER_DETAIL, { id: data._id })),
                    text: i18n.t("notification:purchase_order.createdAndSubmitted.link"),
                },
            )
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, i18n.t("error:request.create.title"))
        },
    })
}

export const useDeletePurchaseOrderMutation = () =>
    useMutation<PurchaseOrder, Error, string>({
        mutationFn: (purchaseOrderId: string) => PurchaseOrderApiService.deletePurchaseOrder(purchaseOrderId),
        onSuccess: async (purchaseOrder) => {
            await invalidatePurchaseOrderDetail(purchaseOrder._id)
            // This will invalidate all purchase order lists
            await invalidatePurchaseOrderLists()
            store.dispatch(refetchTable(TablesEnum.COST_CENTERS))
            await invalidatePerformanceExpensesForPurchaseOrder(purchaseOrder)
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:purchase_order.deleted.title"), t("notification:purchase_order.deleted.message"))
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:purchase_order.delete.title"), t("error:purchase_order.delete.message"))
        },
    })

export const useAutoMatchPurchaseOrderMutation = () =>
    useMutation<AutoMatchPurchaseOrdersResponse, Error, AutoMatchPurchaseOrdersRequest>({
        mutationFn: (request: AutoMatchPurchaseOrdersRequest) => PurchaseOrderApiService.autoMatchInvoiceItems(request),
    })

export const useUpdatePurchaseOrderCommentsMutation = () =>
    useMutation({
        mutationFn: ({ objectId, comments }: { objectId: string; comments: Array<CommentItem> }) => PurchaseOrderApiService.updateComments(objectId, comments),
        onSuccess: (updatedPurchaseOrder: PurchaseOrder, { objectId }) => {
            queryClient.setQueryData(PURCHASE_ORDER_QUERY_KEYS.DETAIL(objectId), (cachedPurchaseOrder: PurchaseOrder) => ({
                ...cachedPurchaseOrder,
                comments: updatedPurchaseOrder.comments,
            }))
        },
    })

export const useCreateTagMutation = () =>
    useMutation<ExpenseTag, Error, { tagName: string; tagColor: ExpenseTagColorEnum }>({
        mutationFn: ({ tagName, tagColor }: { tagName: string; tagColor: ExpenseTagColorEnum }) => ExpenseTagService.createExpenseTag({ tagName, tagColor }),
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:expense_tag.create.title"))
        },
    })

export const useDeleteTagMutation = () =>
    useMutation<ExpenseTag, Error, string>({
        mutationFn: (tagId: string) => ExpenseTagService.deleteExpenseTag(tagId),
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:expense_tag.delete.title"))
        },
    })

export const useAttachTagToPurchaseOrderMutation = () =>
    useMutation<PurchaseOrder, Error, { purchaseOrderId: string; tagId: string }>({
        mutationFn: ({ purchaseOrderId, tagId }: { purchaseOrderId: string; tagId: string }) => PurchaseOrderApiService.attachTagToPurchaseOrder(purchaseOrderId, tagId),
        onSuccess: async (_updatedPurchaseOrder, { purchaseOrderId }) => {
            await invalidatePurchaseOrderDetail(purchaseOrderId)
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:purchase_order.tag_attached.title"))
        },
    })

export const useDetachTagFromPurchaseOrderMutation = () =>
    useMutation<PurchaseOrder, Error, { purchaseOrderId: string; tagId: string }>({
        mutationFn: ({ purchaseOrderId, tagId }: { purchaseOrderId: string; tagId: string }) => PurchaseOrderApiService.detachTagFromPurchaseOrder(purchaseOrderId, tagId),
        onSuccess: async (_updatedPurchaseOrder, { purchaseOrderId }) => {
            await invalidatePurchaseOrderDetail(purchaseOrderId)
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:purchase_order.tag_detached.title"))
        },
    })

// Utility functions for cache management
export const invalidatePurchaseOrderCache = async () => {
    await queryClient.invalidateQueries({
        queryKey: PURCHASE_ORDER_QUERY_KEYS.ALL,
    })
}

export const invalidatePurchaseOrderLists = async () => {
    await queryClient.invalidateQueries({
        queryKey: PURCHASE_ORDER_QUERY_KEYS.LIST({}),
    })
    await queryClient.invalidateQueries({
        queryKey: PURCHASE_ORDER_QUERY_KEYS.LIST_FOR_LINKING({}),
    })
}

export const invalidatePurchaseOrderDetail = async (id: string) => {
    if (!id) {
        return
    }

    await queryClient.invalidateQueries({
        queryKey: PURCHASE_ORDER_QUERY_KEYS.DETAIL(id),
    })
}

const invalidatePerformanceExpensesForPurchaseOrder = async (po: PurchaseOrder) => {
    if (!po?.lineItems) {
        return
    }
    const costCenterIds = getUniqueElements(po.lineItems, "costCenter").map((li: ILineItem) => li.costCenter)
    await invalidatePerformanceExpenses(costCenterIds)
}

export const useBuildApprovalMutation = () =>
    useMutation<Array<ApprovalProcess>, Error, { approvalProcessScopes: Array<ApprovalScopeEnum>; purchaseOrderData: PurchaseOrderApprovalProcessDTO }>({
        mutationFn: ({ approvalProcessScopes, purchaseOrderData }: { approvalProcessScopes: Array<ApprovalScopeEnum>; purchaseOrderData: PurchaseOrderApprovalProcessDTO }) =>
            PurchaseOrderApiService.buildApprovalProcesses(approvalProcessScopes, purchaseOrderData),
    })

export const useExportPurchaseOrderSummaryMutation = () =>
    useMutation<void, Error, { purchaseOrderId: string; language: string }>({
        mutationFn: ({ purchaseOrderId, language }: { purchaseOrderId: string; language: string }) => FileService.exportPurchaseOrderSummary(purchaseOrderId, language),
        onSuccess: () => {
            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                t("notification:purchase_order.summary_exported.title"),
                t("notification:purchase_order.summary_exported.message"),
            )
        },
        onError: (error) => {
            NotificationService.showErrorNotificationBasedOnResponseError(error, t("notification:purchase_order.summary_exported.title"))
        },
    })
